import React, { useState, useEffect } from 'react';
import { fctrApi } from '../services/api';
import GenerateFCTRModal from './GenerateFCTRModal';
import '../styles/Panel.css';

/**
 * Panel for managing FCTR (Foreign Currency Translation Reserve) calculations
 *
 * Features:
 * - View FCTR calculation history for entity
 * - Generate new FCTR workings for flexible periods
 * - Download generated workbooks
 * - Display FCTR movement and FX rates used
 */
function FCTRPanel({ entity, onComplete }) {
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [downloadingTimestamp, setDownloadingTimestamp] = useState(null);
  const [isApplicable, setIsApplicable] = useState(true);

  useEffect(() => {
    if (entity) {
      checkApplicability();
      fetchCalculations();
    }
  }, [entity]);

  const checkApplicability = () => {
    // Check if entity is applicable for FCTR
    const fxRates = entity.fx_rates;
    if (!fxRates) {
      setIsApplicable(false);
      return;
    }

    const baseCurrency = fxRates.base_currency;
    const consolidationCurrency = fxRates.consolidation_currency;

    // FCTR only applicable when currencies differ
    if (baseCurrency === consolidationCurrency) {
      setIsApplicable(false);
    } else {
      setIsApplicable(true);
    }
  };

  const fetchCalculations = async () => {
    if (!entity) return;

    try {
      setLoading(true);
      setError(null);
      const data = await fctrApi.getHistory(entity.name);
      setCalculations(data.calculations || []);
    } catch (err) {
      console.error('Error fetching FCTR calculations:', err);
      setError(
        err.data?.error ||
        err.message ||
        'Failed to load FCTR calculation history'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateComplete = (result) => {
    // Refresh the calculations list
    fetchCalculations();

    if (onComplete) {
      onComplete(result);
    }
  };

  const handleDownload = async (startDate, endDate, timestamp) => {
    try {
      setDownloadingTimestamp(timestamp);
      const blob = await fctrApi.downloadWorkbook(entity.name, startDate, endDate, timestamp);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fctr_${entity.name}_${startDate}_to_${endDate}_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading FCTR workbook:', err);
      setError('Failed to download workbook');
    } finally {
      setDownloadingTimestamp(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (!entity) {
    return (
      <div className="panel">
        <h2>FCTR (Foreign Currency Translation Reserve)</h2>
        <p className="info-text">Please select an entity to view FCTR calculations.</p>
      </div>
    );
  }

  if (!isApplicable) {
    const baseCurrency = entity.fx_rates?.base_currency || 'N/A';
    const consolidationCurrency = entity.fx_rates?.consolidation_currency || 'N/A';

    return (
      <div className="panel">
        <h2>FCTR (Foreign Currency Translation Reserve)</h2>
        <p className="info-text">
          Entity: <strong>{entity.description || entity.name}</strong>
        </p>
        <div className="alert alert-warning">
          <h4>FCTR Not Applicable</h4>
          <p>
            This entity's base currency ({baseCurrency}) is the same as the consolidation currency ({consolidationCurrency}).
            FCTR calculation is only applicable when these currencies differ.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <h2>FCTR (Foreign Currency Translation Reserve)</h2>
      <p className="info-text">
        Entity: <strong>{entity.description || entity.name}</strong>
      </p>

      {/* Info Box */}
      <div className="alert alert-info" style={{ marginBottom: '1.5rem' }}>
        <strong>About FCTR (IndAS 21 & Schedule III):</strong>
        <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          FCTR calculates foreign currency translation differences when converting financial statements
          from <strong>{entity.fx_rates?.base_currency}</strong> to{' '}
          <strong>{entity.fx_rates?.consolidation_currency}</strong>. Monetary items are translated at
          closing rate, non-monetary items at historical rate, and the net translation difference is
          accumulated in Other Equity as per Schedule III requirements.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: '1rem',
              padding: '0.25rem 0.75rem',
              fontSize: '0.875rem',
            }}
            className="btn btn-secondary"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Actions Section */}
      <div className="actions-section" style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="btn btn-primary"
          disabled={loading}
        >
          <span style={{ marginRight: '0.5rem' }}>📊</span>
          Generate FCTR Working
        </button>

        <button
          onClick={fetchCalculations}
          className="btn btn-secondary"
          disabled={loading}
          style={{ marginLeft: '1rem' }}
        >
          <span style={{ marginRight: '0.5rem' }}>🔄</span>
          Refresh
        </button>
      </div>

      {/* Calculation History Section */}
      <div className="history-section">
        <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', color: '#374151' }}>
          Calculation History
        </h3>

        {loading && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            <p>Loading FCTR calculations...</p>
          </div>
        )}

        {!loading && calculations.length === 0 && (
          <div className="empty-state" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            <p>No FCTR calculations found for this entity.</p>
            <p style={{ fontSize: '0.9rem' }}>Click "Generate FCTR Working" to create one.</p>
          </div>
        )}

        {!loading && calculations.length > 0 && (
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Period</th>
                  <th>Base Currency</th>
                  <th>Consol. Currency</th>
                  <th>FCTR Movement</th>
                  <th>FX Rates</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {calculations.map((calc) => (
                  <tr key={calc.timestamp}>
                    <td>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {calc.timestamp}
                      </div>
                      {calc.upload_time && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                          {formatDate(calc.upload_time)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.875rem' }}>
                        {formatDate(calc.start_date)} to
                      </div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        {formatDate(calc.end_date)}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: '500' }}>
                      {calc.base_currency || 'N/A'}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: '500' }}>
                      {calc.consolidation_currency || 'N/A'}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontWeight: '600',
                        color: calc.fctr_movement >= 0 ? '#059669' : '#DC2626',
                        fontFamily: 'monospace',
                      }}
                    >
                      {formatNumber(calc.fctr_movement)}
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>
                      <div>H: {calc.historical_rate?.toFixed(4) || 'N/A'}</div>
                      <div>C: {calc.closing_rate?.toFixed(4) || 'N/A'}</div>
                      <div>A: {calc.average_rate?.toFixed(4) || 'N/A'}</div>
                    </td>
                    <td>
                      <button
                        onClick={() => handleDownload(calc.start_date, calc.end_date, calc.timestamp)}
                        className="btn btn-sm btn-primary"
                        disabled={downloadingTimestamp === calc.timestamp}
                        title="Download Excel workbook"
                      >
                        {downloadingTimestamp === calc.timestamp ? '⏳' : '📥'}
                        {downloadingTimestamp === calc.timestamp ? ' Downloading...' : ' Download'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Calculation Summary */}
      {!loading && calculations.length > 0 && (
        <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
          Total calculations: {calculations.length}
        </div>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <GenerateFCTRModal
          entity={entity}
          onClose={() => setShowGenerateModal(false)}
          onComplete={handleGenerateComplete}
        />
      )}
    </div>
  );
}

export default FCTRPanel;
